import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, graphScopes, apiScopes } from "../services/authConfig";
const AuthContext = createContext({
    graphToken: null,
    apiToken: null,
    accessToken: null,
    userEmail: "",
    userName: "",
    realUserEmail: "",
    realUserName: "",
    isAuthenticated: false,
    isLoading: true,
    error: null,
    login: async () => { },
});
const msalInstance = new PublicClientApplication(msalConfig);
export const AuthProvider = ({ children }) => {
    const [graphToken, setGraphToken] = useState(null);
    const [apiToken, setApiToken] = useState(null);
    const [account, setAccount] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const acquireToken = useCallback(async (acct) => {
        const silentAccount = acct || undefined;
        // Acquérir graphToken et apiToken en parallèle
        const [graphResult, apiResult] = await Promise.allSettled([
            // Graph token
            msalInstance.acquireTokenSilent({ scopes: graphScopes, account: silentAccount })
                .catch(async (err) => {
                if (err instanceof InteractionRequiredAuthError) {
                    return msalInstance.acquireTokenPopup({ scopes: graphScopes });
                }
                throw err;
            }),
            // API token — échec non bloquant si le scope n'est pas encore consenti
            msalInstance.acquireTokenSilent({ scopes: apiScopes, account: silentAccount })
                .catch(async (err) => {
                if (err instanceof InteractionRequiredAuthError) {
                    return msalInstance.acquireTokenPopup({ scopes: apiScopes });
                }
                throw err;
            }),
        ]);
        if (graphResult.status === "rejected") {
            throw graphResult.reason;
        }
        const gToken = graphResult.value.accessToken;
        setGraphToken(gToken);
        setAccount(graphResult.value.account);
        let aToken = null;
        if (apiResult.status === "fulfilled") {
            aToken = apiResult.value.accessToken;
            setApiToken(aToken);
        }
        else {
            console.warn("[AuthContext] apiToken non acquis (scope non consenti ?):", apiResult.reason);
            setApiToken(null);
        }
        return { graphToken: gToken, apiToken: aToken };
    }, []);
    useEffect(() => {
        (async () => {
            try {
                await msalInstance.initialize();
                const response = await msalInstance.handleRedirectPromise();
                if (response) {
                    setAccount(response.account);
                    await acquireToken(response.account);
                }
                else {
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length > 0) {
                        await acquireToken(accounts[0]);
                    }
                }
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Erreur d'authentification");
            }
            finally {
                setIsLoading(false);
            }
        })();
    }, [acquireToken]);
    const login = useCallback(async () => {
        try {
            setError(null);
            const result = await msalInstance.loginPopup({ scopes: graphScopes });
            setAccount(result.account);
            await acquireToken(result.account);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur de connexion");
        }
    }, [acquireToken]);
    return (_jsx(AuthContext.Provider, { value: {
            graphToken,
            apiToken,
            accessToken: graphToken, // backward compat
            userEmail: account?.username || "",
            userName: account?.name || "",
            realUserEmail: account?.username || "",
            realUserName: account?.name || "",
            isAuthenticated: !!graphToken,
            isLoading,
            error,
            login,
        }, children: children }));
};
export const useAuth = () => useContext(AuthContext);
